import React from "react";
import { Grid, Skeleton } from "@mui/material";
import { MuiBox, MuiTypography } from "components/atoms";
import { useTranslation } from "react-i18next";

const HomePageSkeleton = ({ countPerSection = 4 }) => {
  const { t } = useTranslation();

  const FileCardSkeleton = () => (
    <MuiBox
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "10px",
        border: "1px solid",
        borderColor: "background.gray400",
        overflow: "hidden",
        bgcolor: "background.white",
      }}
    >
      <Skeleton
        variant="rounded"
        width="100%"
        height={220}
        sx={{ borderRadius: 0 }}
      />
      <MuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          p: "10px 12px",
        }}
      >
        <MuiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Skeleton variant="text" width="75%" height={16} />
          <Skeleton variant="text" width="50%" height={12} />
        </MuiBox>
        <Skeleton
          variant="circular"
          width={20}
          height={20}
          sx={{ flexShrink: 0 }}
        />
      </MuiBox>
    </MuiBox>
  );

  // Large folder card (taller than list-style FolderCard)
  const LargeFolderCardSkeleton = () => (
    <MuiBox
      sx={{
        display: "flex",
        flexDirection: "column",
        borderRadius: "10px",
        border: "1px solid",
        borderColor: "background.gray400",
        overflow: "hidden",
        bgcolor: "background.white",
      }}
    >
      <MuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: 220,
          bgcolor: "background.blue200",
        }}
      >
        <Skeleton
          variant="rounded"
          width={48}
          height={48}
          sx={{ borderRadius: "8px" }}
        />
      </MuiBox>
      <MuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "8px",
          p: "10px 12px",
        }}
      >
        <MuiBox
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "4px",
            minWidth: 0,
            flex: 1,
          }}
        >
          <Skeleton variant="text" width="70%" height={16} />
          <Skeleton variant="text" width="45%" height={12} />
        </MuiBox>
        <Skeleton
          variant="circular"
          width={20}
          height={20}
          sx={{ flexShrink: 0 }}
        />
      </MuiBox>
    </MuiBox>
  );

  const Section = ({ title, children }) => (
    <MuiBox sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
      <MuiBox
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "16px",
        }}
      >
        <MuiTypography fontWeight="600" variant="18" color="text.darkBlack">
          {title}
        </MuiTypography>
        <Skeleton variant="text" width={70} height={18} />
      </MuiBox>
      <Grid container spacing={3}>
        {children}
      </Grid>
    </MuiBox>
  );

  return (
    <MuiBox
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "32px",
        width: "100%",
      }}
    >
      <MuiTypography fontWeight="400" variant="24" color="text.darkBlack">
        {t("home.welcomeToDrive")}
      </MuiTypography>
      {/* Shared with me – mix of large folder + file */}
      <Section title={t("common.sharedWithMe")}>
        {Array.from({ length: countPerSection }).map((_, i) => (
          <Grid item key={`shared-${i}`} xs={12} sm={6} md={4} lg={3}>
            {i % 2 === 0 ? <LargeFolderCardSkeleton /> : <FileCardSkeleton />}
          </Grid>
        ))}
      </Section>

      {/* My Folders */}
      <Section title={t("common.myFolders")}>
        {Array.from({ length: countPerSection }).map((_, i) => (
          <Grid item key={`folder-${i}`} xs={12} sm={6} md={4} lg={3}>
            <LargeFolderCardSkeleton />
          </Grid>
        ))}
      </Section>

      {/* My Files */}
      <Section title={t("common.myFiles")}>
        {Array.from({ length: countPerSection }).map((_, i) => (
          <Grid item key={`file-${i}`} xs={12} sm={6} md={4} lg={3}>
            <FileCardSkeleton />
          </Grid>
        ))}
      </Section>

      {/* Starred */}
      <Section title={t("common.starred")}>
        {Array.from({ length: countPerSection }).map((_, i) => (
          <Grid item key={`star-${i}`} xs={12} sm={6} md={4} lg={3}>
            {i % 2 === 0 ? <LargeFolderCardSkeleton /> : <FileCardSkeleton />}
          </Grid>
        ))}
      </Section>
    </MuiBox>
  );
};

export default HomePageSkeleton;
